'use client';
import { YellowButton } from '@/components/button/button';
import { config } from '@/config/environment';
import {
  Box,
  Container,
  Dialog,
  GlobalStyles,
  Grid,
  IconButton,
  Link,
  styled,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import React, { useEffect, useState } from 'react';
import { useVideoAskModal } from '@/components/video-ask/lib/video-ask-client';
import { sectionSpacingCss } from '../section-spacing';
import { useGetExperienceByIdLazyQuery } from '@/generated/graphql';
import ExperienceCard from '@/components/cards/experience-card/experience-card';
import { useRouter } from 'next/navigation';

const HeaderBox = styled(Box)`
  ${sectionSpacingCss}
  height: auto;
  min-height: 30vh;
  margin: 0;
  position: relative;
  overflow: hidden;
  color: white;
  width: 100%;
  display: flex;
  align-items: flex-start;
  justify-content: center;
`;
const ShareMomentsHeader = styled(Typography)`
  margin: 0 0 24px;
  padding-left: clamp(16px, 6vw, 100px);
  font-family: var(--font-cooper-light), sans-serif;
  font-weight: 300;
  font-size: 67px;
  line-height: 1.35;
  color: #ffffff;
  text-align: start;
  z-index: 1;
  margin-bottom: 32px;
  text-shadow:
    0 0 20px rgba(31, 21, 33),
    0 0 20px rgba(31, 21, 33),
    0 0 20px rgba(31, 21, 33);

  // Mobile screens
  @media (max-width: 768px) {
    width: 100%;
    padding-left: 0;
    font-size: 45px;
    text-align: center;
    margin-bottom: 1px;
    text-align: center;
  }
`;
const ShareMomentsP = styled(Typography)`
  margin: 16px 0 32px;
  font-family: var(--font-work-sans), sans-serif;
  font-size: 18px;
  line-height: 160%;
  color: #ffffff;
  text-align: start;
  margin-bottom: 32px;
  width: 100%;
  max-width: 500px;
  text-shadow:
    0 0 20px rgba(31, 21, 33),
    0 0 20px rgba(31, 21, 33),
    0 0 20px rgba(31, 21, 33);
  @media (max-width: 768px) {
    width: 100%;
    margin-top: 8px;
    text-align: center;
    margin-left: auto;
    margin-right: auto;
  }
`;

const CTAButton = styled('a')`
  display: inline-block;
  font-family:
    work sans,
    sans-serif;
  background: #ffd02f;
  color: #000;
  font-weight: 600 !important;
  font-size: 20px;
  padding: 13px 32px;
  border-radius: 999px;
  text-decoration: none;
  box-shadow: 0 6px 18px rgba(0, 0, 0, 0.12);
  transition: background-color 160ms ease;
  :hover {
    background-color: #f0926e;
  }
`;

const StyledDialog = styled(Dialog)`
  .MuiDialog-paper {
    background: rgba(59, 25, 50, 0.95);
    border-radius: 18px;
    padding: 20px;
    max-width: 900px;
    width: 90%;
    position: relative;
  }
  .MuiBackdrop-root {
    backdrop-filter: blur(4px);
    background-color: rgba(0, 0, 0, 0.6);
  }
`;

const ModalContent = styled('div')`
  padding: 20px;
  color: white;
`;

const ModalTitle = styled(Typography)`
  font-family: var(--font-cooper-light), sans-serif;
  font-size: 32px;
  text-align: center;
  color: white;
  margin-bottom: 30px;
  @media (max-width: 600px) {
    font-size: 24px;
    margin-bottom: 20px;
  }
`;

const CloseButton = styled(IconButton)`
  position: absolute;
  top: 16px;
  right: 16px;
  color: white;
  z-index: 1;
  &:hover {
    background-color: rgba(255, 255, 255, 0.1);
  }
`;

const ExperienceGrid = styled(Grid)`
  margin-top: 16px;
`;

const LoadingText = styled(Typography)`
  text-align: center;
  color: white;
  padding: 40px;
`;

// Helper function to extract experience ID from URL
const extractExperienceId = (url: string): string | null => {
  if (!url) return null;
  try {
    const match = url.match(/\/experiences\/([a-f0-9-]+)/i);
    return match ? match[1] : null;
  } catch {
    return null;
  }
};

export const Header: React.FC<{ data?: any }> = ({ data }) => {
  const archiveMode = config.platformMode === 'archive';
  const heading = archiveMode ? 'Coaching with Curiko' : data?.heading;
  const subHeading = archiveMode ? '' : data?.subheading;
  const description = archiveMode ? (
    <>
      Curiko Coaching helped you to explore your values, grow your strengths, and take small steps
      forward. It was 100% free, thanks to our partners and supporters.
    </>
  ) : (
    data?.description
  );
  const ctaLabel = data?.ctaLabel;
  const ctaUrl = data?.ctaUrl || '';
  const ctaUrl2 = data?.ctaUrl2 || '';
  const videoAskUrl = data?.videoAskUrl || '';
  const router = useRouter();

  const [modalOpen, setModalOpen] = useState(false);
  const [openVideoAsk] = useVideoAskModal(
    videoAskUrl || ctaUrl,
    {},
    { modalType: data?.fullscreen ? 'Fullscreen' : 'Side' },
  );

  // Extract experience IDs from both URLs
  const experienceId1 = ctaUrl ? extractExperienceId(ctaUrl) : null;
  const experienceId2 = ctaUrl2 ? extractExperienceId(ctaUrl2) : null;

  // Use lazy queries to fetch experiences when needed
  const [loadExperience1, { data: exp1Data, loading: loading1 }] = useGetExperienceByIdLazyQuery({
    fetchPolicy: 'cache-first',
  });

  const [loadExperience2, { data: exp2Data, loading: loading2 }] = useGetExperienceByIdLazyQuery({
    fetchPolicy: 'cache-first',
  });

  const handleClick: React.MouseEventHandler<HTMLAnchorElement> = e => {
    e.preventDefault(); // Always prevent default navigation

    // If both URLs are present, show the experience selection modal
    if (ctaUrl && ctaUrl2 && experienceId1 && experienceId2) {
      // Load both experiences
      loadExperience1({ variables: { id: experienceId1 } });
      loadExperience2({ variables: { id: experienceId2 } });
      setModalOpen(true);
      return;
    }

    // If there's a separate VideoAsk URL, open the VideoAsk modal
    if (videoAskUrl && videoAskUrl !== ctaUrl) {
      try {
        openVideoAsk();
      } catch (err) {
        // fallback to natural navigation if modal can't open
        if (ctaUrl) {
          router.push(ctaUrl);
        }
      }
      return;
    }

    // Otherwise, navigate to ctaUrl (single URL behavior)
    if (ctaUrl) {
      router.push(ctaUrl);
    }
  };

  const handleModalClose = () => {
    setModalOpen(false);
  };

  const handleExperienceSelect = (url: string) => {
    setModalOpen(false);
    router.push(url);
  };

  const handleKeyDown = (url: string) => (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleExperienceSelect(url);
    }
  };

  const experience1 = exp1Data?.experience;
  const experience2 = exp2Data?.experience;
  const isLoading = loading1 || loading2;

  return (
    <HeaderBox justifyContent="center">
      <Container
        maxWidth="lg"
        sx={{
          mx: 'auto',
          minHeight: '50px',
          position: 'relative',
          zIndex: 99,
        }}
      >
        <Grid container spacing={1} alignItems="center">
          <Grid item xs={12} md={6}>
            <ShareMomentsHeader>
              {heading}
              <br />
              {subHeading}
            </ShareMomentsHeader>
          </Grid>
          <Grid item xs={12} md={6}>
            <ShareMomentsP>{description}</ShareMomentsP>
            <Box
              sx={{
                display: {
                  justifyContent: 'center',
                  alignItems: 'center',
                  sm: 'block',
                },
              }}
            >
              {!archiveMode && (
                <CTAButton href={ctaUrl || '#book-coach'} role="button" onClick={handleClick}>
                  {ctaLabel || 'Connect with a coach'}
                </CTAButton>
              )}
            </Box>
          </Grid>
        </Grid>
      </Container>

      {/* Experience Selection Modal */}
      <StyledDialog
        open={modalOpen}
        onClose={handleModalClose}
        maxWidth="md"
        fullWidth
        aria-labelledby="experience-selection-dialog"
      >
        <CloseButton onClick={handleModalClose} aria-label="Close dialog">
          <CloseIcon />
        </CloseButton>
        <ModalContent>
          <ModalTitle variant="h2" id="experience-selection-dialog">
            {heading} {subHeading}
          </ModalTitle>

          {isLoading ? (
            <LoadingText>Loading experiences...</LoadingText>
          ) : (
            <ExperienceGrid container spacing={3} justifyContent="center">
              {experience1 && (
                <Grid item xs={12} sm={6}>
                  <div
                    role="button"
                    tabIndex={0}
                    onClick={() => handleExperienceSelect(ctaUrl)}
                    onKeyDown={handleKeyDown(ctaUrl)}
                    style={{ cursor: 'pointer' }}
                  >
                    <ExperienceCard experience={experience1 as any} />
                  </div>
                </Grid>
              )}
              {experience2 && (
                <Grid item xs={12} sm={6}>
                  <div
                    role="button"
                    tabIndex={0}
                    onClick={() => handleExperienceSelect(ctaUrl2)}
                    onKeyDown={handleKeyDown(ctaUrl2)}
                    style={{ cursor: 'pointer' }}
                  >
                    <ExperienceCard experience={experience2 as any} />
                  </div>
                </Grid>
              )}
            </ExperienceGrid>
          )}
        </ModalContent>
      </StyledDialog>
    </HeaderBox>
  );
};
